import { useState } from 'react';
import { MODULES, getModule } from '@onepos/shared';
import type { ModuleDef } from '@onepos/shared';
import { Alert, Badge, Button, Card, Icon, Modal, Switch, useAction, useAsync } from '@onepos/ui';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { fmtMoney } from '../../lib/format';
import type { Plan, TenantDetail } from '../../lib/types';

export function ModulesTab({ data, reload }: { data: TenantDetail; reload: () => void }) {
  const me = useMe();
  const readOnly = me.role === 'billing';
  const [pending, setPending] = useState<string | null>(null);
  // Tarifga kirmagan bo'limni yoqish — tasdiq bilan ("tarifdan tashqari": sinov, namoyish yoki alohida kelishuv)
  const [outside, setOutside] = useState<ModuleDef | null>(null);
  const state = new Map(data.modules.map((m) => [m.key, m]));
  // Tarifdan tashqari bo'lim narxi (Q-079): narxi qo'yilgan bo'lsa — oylik summaga qo'shiladi
  const plans = useAsync(() => api.rpc<Plan[]>('platform_plans'), []);
  const addonPrice = (key: string) => plans.data?.find((p) => p.id === data.plan.id)?.addon_prices?.[`module.${key}`] ?? null;
  const act = useAction(async (key: string, enabled: boolean, locked: boolean | null) => {
    setPending(key);
    try {
      await api.rpc('platform_set_module', { p_tenant: data.tenant.id, p_key: key, p_enabled: enabled, p_locked: locked });
      reload();
    } finally {
      setPending(null);
    }
  });

  return (
    <Card
      title="Bo‘limlar (modullar)"
      actions={<span className="cn-muted">Qulflangan bo‘limni ega o‘zgartira olmaydi</span>}
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <ul className="cn-settings">
        {(MODULES as readonly ModuleDef[]).map((m) => {
          const cur = state.get(m.key);
          const enabled = cur?.enabled ?? false;
          const locked = cur?.locked ?? false;
          const inPlan = data.plan.modules.includes(m.key);
          const planned = m.status !== 'available';
          const always = m.alwaysOn === true;
          // Platforma tarifdan tashqari ham yoqa oladi (server ruxsat beradi, jurnalga yoziladi); ega — faqat tarifdagini
          const blocked = readOnly || pending !== null || always || (!enabled && planned);
          return (
            <li key={m.key} className="cn-settings__row">
              <div className="cn-settings__main">
                <Switch
                  label={m.label}
                  checked={enabled}
                  disabled={blocked}
                  onChange={(next) => (next && !inPlan ? setOutside(m) : void act.run(m.key, next, null))}
                  description={
                    <>
                      {m.description}
                      <span className="cn-tags" style={{ marginTop: 6 }}>
                        {always && <Badge tone="neutral">har doim yoqiq</Badge>}
                        {planned && <Badge tone="neutral">tez orada</Badge>}
                        {!planned && !inPlan && <Badge tone="warning">{enabled ? 'tarifdan tashqari yoqilgan' : 'tarifda yo‘q'}</Badge>}
                        {!planned && !inPlan && addonPrice(m.key) !== null && <Badge tone="info">qo‘shimcha · {fmtMoney(addonPrice(m.key)!)}/oy</Badge>}
                        {locked && <Badge tone="info">qulflangan</Badge>}
                        {m.requires?.length ? (
                          <span className="cn-muted">talab: {m.requires.map((r) => getModule(r)?.label ?? r).join(', ')}</span>
                        ) : null}
                      </span>
                    </>
                  }
                />
              </div>
              {!always && !planned && (
                <Button
                  size="sm"
                  variant="ghost"
                  iconOnly
                  disabled={readOnly || pending !== null || !cur}
                  aria-label={locked ? `${m.label}: qulfni ochish` : `${m.label}: qulflash`}
                  title={locked ? 'Qulfni ochish' : 'Qulflash (ega o‘zgartira olmaydi)'}
                  onClick={() => void act.run(m.key, enabled, !locked)}
                >
                  <Icon name={locked ? 'lock' : 'unlock'} />
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {outside && (
        <Modal
          open
          title={`${outside.label}: tarifdan tashqari yoqish`}
          onClose={() => setOutside(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setOutside(null)}>
                Bekor qilish
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const key = outside.key;
                  setOutside(null);
                  void act.run(key, true, null);
                }}
              >
                Baribir yoqish
              </Button>
            </>
          }
        >
          <p>
            «{outside.label}» restoranning «{data.plan.label}» tarifiga kirmaydi. Sinov, namoyish yoki alohida kelishuv uchun yoqish mumkin — amal jurnalga
            yoziladi.
          </p>
          {addonPrice(outside.key) !== null && (
            <Alert tone="info">
              Bu bo‘lim qo‘shimcha sifatida sotiladi: yoqilsa, oylik summaga {fmtMoney(addonPrice(outside.key)!)} qo‘shiladi (imtiyozli
              restoranda hisoblanmaydi).
            </Alert>
          )}
          <p className="cn-muted">Restoran tarifi almashtirilsa: yangi tarifda narxi qo‘yilgan bo‘lim qo‘shimcha bo‘lib qoladi, qolganlari o‘chiriladi.</p>
        </Modal>
      )}
    </Card>
  );
}
